import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, FileText, Target, ShieldCheck, Zap, BookOpen, ExternalLink, Moon, Sun } from 'lucide-react';
import Logo from '../../components/common/Logo';
import { useTheme } from '../../context/ThemeContext';

const DocsLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const { theme, toggleMode } = useTheme();

    const isDark = theme.mode === 'dark';

    const navItems = [
        { title: 'Hızlı Başlangıç', path: '/getting-started', icon: Zap },
        { title: 'Whitepaper', path: '/whitepaper', icon: FileText },
        { title: 'Roadmap', path: '/roadmap', icon: Target },
        { title: 'API Referansı', path: '/api', icon: BookOpen },
        { title: 'Güvenlik & KVKK', path: '/security', icon: ShieldCheck },
    ];

    const isActive = (path) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-main)', color: 'var(--text-main)' }}>
            {/* Top Navigation */}
            <header style={{
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 1.5rem',
                background: 'var(--bg-card)',
                borderBottom: '1px solid var(--border)',
                position: 'sticky',
                top: 0,
                zIndex: 50
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        style={{ display: 'none', background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer' }}
                        className="mobile-menu-btn"
                    >
                        <Menu size={24} />
                    </button>
                    <a href="https://ugra.io" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, color: 'var(--primary)', textDecoration: 'none', fontSize: '1.25rem' }}>
                        <Logo size={28} /> Dokümantasyon
                    </a>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <button onClick={toggleMode} style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    <a href="https://ugra.io" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
                        ugra.io <ExternalLink size={16} />
                    </a>
                </div>
            </header>

            <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
                {/* Overlay for mobile sidebar */}
                {sidebarOpen && (
                    <div
                        onClick={() => setSidebarOpen(false)}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }}
                        className="sidebar-overlay"
                    />
                )}

                {/* Left Sidebar */}
                <aside
                    className={`docs-sidebar ${sidebarOpen ? 'open' : ''}`}
                    style={{
                        width: '280px',
                        flexShrink: 0,
                        borderRight: '1px solid var(--border)',
                        background: 'var(--bg-card)',
                        padding: '2rem 1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        overflowY: 'auto',
                        height: 'calc(100vh - 64px)',
                        position: 'sticky',
                        top: '64px',
                        transition: 'transform 0.3s ease'
                    }}
                >
                    <div style={{
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.5rem',
                        padding: '0 0.75rem'
                    }}>
                        Hızlı Erişim
                    </div>
                    {navItems.map(item => {
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    color: active ? 'var(--primary)' : 'var(--text-main)',
                                    background: active ? 'rgba(var(--primary-rgb), 0.1)' : 'transparent',
                                    fontWeight: active ? 700 : 500,
                                    transition: 'all 0.2s',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <item.icon size={18} style={{ opacity: active ? 1 : 0.7 }} />
                                {item.title}
                            </Link>
                        );
                    })}
                </aside>

                {/* Main Content Area */}
                <main style={{ flex: 1, padding: '3rem 4rem', maxWidth: '1000px', margin: '0 auto', width: '100%' }} className="docs-main">
                    <Outlet />
                </main>
            </div>

            <style>{`
                @media (max-width: 768px) {
                    .mobile-menu-btn { display: block !important; }
                    .docs-sidebar {
                        position: fixed !important;
                        left: 0;
                        z-index: 45;
                        transform: translateX(-100%);
                    }
                    .docs-sidebar.open {
                        transform: translateX(0);
                    }
                    .docs-main {
                        padding: 2rem 1.5rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default DocsLayout;
